import type { ReactNode } from 'react';

import { AppIcons, type AppIconName } from '@web/ui/core/atoms';
import { PopoverMenu } from '@web/ui/core/molecules/popover_menu';
import { twcn } from '@web/ui/core/utils';

interface FilterSelectOption<V extends string> {
  value: V;
  label: ReactNode;
}

interface FilterSelectProps<V extends string> {
  label: string;
  icon: AppIconName;
  value: V;
  setValue: (v: V) => void;
  options: FilterSelectOption<V>[];
  /** Render the trigger value + options in monospaced typography. */
  mono?: boolean;
}

/**
 * FilterSelect — pill-shaped dropdown bound to a single value. First
 * option is treated as the neutral default; selecting any other value
 * lights the trigger in the brand tint. Used for toolbar filters
 * (domain, role, status) and the sort selector — anywhere a labelled
 * "X: value ▾" affordance opens a vertical option list.
 */
export function FilterSelect<V extends string>({
  label,
  icon,
  value,
  setValue,
  options,
  mono,
}: Readonly<FilterSelectProps<V>>): React.JSX.Element {
  const current = options.find((o) => o.value === value) ?? options[0];
  const isOn = current !== undefined && current !== options[0];
  return (
    <PopoverMenu
      side="bottom"
      align="start"
      width="md"
      renderTrigger={({ onClick, open }) => (
        <button
          type="button"
          onClick={onClick}
          aria-haspopup="menu"
          aria-expanded={open}
          className={twcn(
            'inline-flex h-row-h cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-meta text-foreground transition-colors hover:border-border-strong',
            isOn && 'border-primary/30 bg-primary-tint text-primary-ink',
          )}
        >
          <AppIcons
            name={icon}
            size={13}
            className={twcn('text-muted-foreground', isOn && 'text-primary')}
          />
          <span className={twcn('text-muted-foreground', isOn && 'text-primary-ink/85')}>
            {label}:
          </span>
          <span className={twcn('font-medium', mono && 'font-mono text-card-id')}>
            {current?.label ?? value}
          </span>
          <AppIcons name="caret-down" size={11} className="text-faint-foreground" />
        </button>
      )}
    >
      {({ close }) => (
        <div className="flex flex-col">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                setValue(o.value);
                close();
              }}
              className="flex w-full cursor-pointer items-center gap-2 rounded-sm border-0 bg-transparent px-2 py-1.5 text-left text-meta text-foreground transition-colors hover:bg-muted"
            >
              <span className={twcn(mono && 'font-mono text-card-id')}>{o.label}</span>
              {o.value === value ? (
                <AppIcons name="check" size={12} className="ml-auto text-primary" />
              ) : null}
            </button>
          ))}
        </div>
      )}
    </PopoverMenu>
  );
}
